import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    useAppSelector,
    useTheme,
    Box,
    Typography,
    Divider,
    Stack,
} from '../../../../base';
import { reportsSelectors } from '../../../../actions';
import {
    TimeToHireChartType,
    IClientTimeToHireJobData,
    getReportPeriodLabel,
    getJobMetricChartData,
} from '../../../../common';
import ChartTypeToggle from '../ChartTypeToggle';
import TimeToHireEmptyState from '../TimeToHireEmptyState';
import JobBarChart from './JobBarChart';
import JobLineChart from './JobLineChart';
import JobPieChart from './JobPieChart';
import { StyledHeader } from './JobSpecificView.styles';

const JobSpecificView = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [chartType, setChartType] = useState<TimeToHireChartType>(
        TimeToHireChartType.PIE
    );

    const timeToHireData = useAppSelector(
        reportsSelectors.getTimeToHireData
    ) as IClientTimeToHireJobData;

    if (!timeToHireData || timeToHireData.view !== 'job') return null;

    const dateRangeLabel = getReportPeriodLabel(
        {
            startDate: timeToHireData.startDate,
            endDate: timeToHireData.endDate,
            timeFrame: timeToHireData.timeFrame,
        },
        t
    );

    const totalDays = getJobMetricChartData(timeToHireData, t).reduce(
        (sum, datum) => sum + datum.days,
        0
    );
    const isEmpty = totalDays === 0;

    return (
        <Stack sx={{ flex: 1 }}>
            <StyledHeader container>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {t('reportsPage.timeToHireReport.jobSpecific.title')}
                    </Typography>
                    <Typography variant="body2" color={theme.palette.grey[500]}>
                        {`${timeToHireData.jobTitle} (${dateRangeLabel})`}
                    </Typography>
                </Box>
                {!isEmpty && (
                    <ChartTypeToggle
                        activeType={chartType}
                        onChange={setChartType}
                    />
                )}
            </StyledHeader>

            <Divider sx={{ borderColor: theme.palette.grey[300] }} />
            {isEmpty ? (
                <TimeToHireEmptyState />
            ) : (
                <Box p={2}>
                    {chartType === TimeToHireChartType.PIE && (
                        <JobPieChart data={timeToHireData} />
                    )}
                    {chartType === TimeToHireChartType.BAR && (
                        <JobBarChart data={timeToHireData} />
                    )}
                    {chartType === TimeToHireChartType.LINE && (
                        <JobLineChart data={timeToHireData} />
                    )}
                </Box>
            )}
        </Stack>
    );
};

export default JobSpecificView;
